import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   * Preserves block content (e.g. custom liquid with specs) in case the server response
   * omits it or parsing loses it during variant switch.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    // Preserve block content (custom liquid, att-wrapper, etc.) – these can disappear when
    // the whole media-gallery is replaced during variant switch
    const blockSelectors = 'slideshow-component, zoom-dialog, slideshow-controls, slideshow-arrows, ul.media-gallery__grid';
    const blocksToPreserve = Array.from(this.children).filter((child) => {
      if (child.matches?.(blockSelectors)) return false;
      if (child.querySelector?.('.att-wrapper')) return true;
      const cls = typeof child.className === 'string' ? child.className : child.getAttribute?.('class') ?? '';
      return cls.includes('shopify-block') || cls.includes('ai-custom-liquid');
    });

    this.replaceWith(newMediaGallery);

    // Re-append preserved blocks if the replacement doesn't already have them
    if (blocksToPreserve.length > 0 && !newMediaGallery.querySelector('.att-wrapper')) {
      blocksToPreserve.forEach((block) => newMediaGallery.appendChild(block));
    }
    // variant:updated is dispatched from variant-picker (requestAnimationFrame) after all
    // variant:update handlers — including product-price — so the DOM is final.
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
